import { createApp } from 'vue'
import { createPinia } from 'pinia'
import FlowManagerView from '../../views/FlowManagerView.vue'
import { installUserSettings } from '../../userSettings/installUserSettings'
import { perlingoI18n } from '../../i18n'
import type { FlowGraphV1 } from '../flowchart/flowGraph'
import '../../styles/perlingo-ui.css'

export type MountFlowEditorOptions = {
  /** Initial graph to load (skips WordPress fetch). */
  initialGraph?: FlowGraphV1
  /** 'level' = full editor; 'macro' = macro-start/end mode. Default: 'level'. */
  nodeMode?: 'level' | 'macro'
  /** Host-supplied course macros available to macro-call nodes. */
  externalMacros?: Array<{ id: string; name: string; flowGraph: FlowGraphV1 }>
  /** Required: called when the user saves. Overlay unmounts automatically after this. */
  onSave: (graph: FlowGraphV1) => void
  /** Optional: called when the user cancels. Overlay unmounts automatically. */
  onCancel?: () => void
  /** Deck ID to use for the live-preview panel. */
  previewDeckId?: string | number
  /** REST root URL for the preview panel (e.g. window.PLC_BOOTSTRAP.restRoot). */
  previewRestRoot?: string
  /** WP REST nonce for the preview panel (e.g. window.PLC_BOOTSTRAP.nonce). */
  previewNonce?: string
}

export type MountFlowEditorHandle = {
  unmount: () => void
}

export function mountFlowEditor(options: MountFlowEditorOptions): MountFlowEditorHandle {
  const adminBarEl = document.getElementById('wpadminbar')
  const adminBarH = adminBarEl ? Math.round(adminBarEl.getBoundingClientRect().height) : 0

  const adminMenuEl = document.getElementById('adminmenuwrap')
  const sidebarW = adminMenuEl ? Math.round(adminMenuEl.getBoundingClientRect().width) : 0

  const overlay = document.createElement('div')
  overlay.style.cssText =
    `position:fixed;top:${adminBarH}px;left:${sidebarW}px;right:0;bottom:0;z-index:99998;background:#f0f0f1;overflow:hidden;padding:0.75rem 1rem;box-sizing:border-box;`
  document.body.appendChild(overlay)

  const prevBodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'

  let done = false

  const doUnmount = () => {
    if (done) return
    done = true
    app.unmount()
    overlay.remove()
    document.body.style.overflow = prevBodyOverflow
  }

  const previewContext =
    options.previewDeckId && options.previewRestRoot && options.previewNonce
      ? { deckId: options.previewDeckId, restUrl: options.previewRestRoot, nonce: options.previewNonce }
      : undefined

  const pinia = createPinia()
  const app = createApp(FlowManagerView, {
    initialGraph: options.initialGraph,
    nodeMode: options.nodeMode ?? 'level',
    externalMacros: options.externalMacros ?? [],
    previewContext,
    onSave: (graph: FlowGraphV1) => {
      options.onSave(graph)
      doUnmount()
    },
    onCancel: () => {
      options.onCancel?.()
      doUnmount()
    },
  })

  app.use(pinia)
  installUserSettings(app, pinia)
  app.use(perlingoI18n)
  app.mount(overlay)

  return { unmount: doUnmount }
}
